import { useState } from 'react';
import TopBar from '@/components/feature/TopBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { siteConfig } from '@/mocks/data';

export default function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const whatsappHref = `https://wa.me/${siteConfig.whatsappNumber}?text=${encodeURIComponent('Merhaba, Atak Sistem Enerji web siteniz üzerinden ulaşıyorum. Bilgi almak istiyorum.')}`;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9jlcimc26n1c7c5q7a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setSubmitted(true);
        setFormStatus('success');
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : 'Bir hata oluştu, lütfen tekrar deneyin.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Bağlantı hatası. Lütfen daha sonra tekrar deneyin.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <TopBar />
      <Navbar />
      <main>
        <section className="py-16 md:py-20 bg-secondary-800">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-2xl md:text-5xl text-background-50 mb-4">İletişim</h1>
            <p className="text-background-50/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              Bizimle iletişime geçin, size uygun sistemi birlikte belirleyelim.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 max-w-5xl mx-auto">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">İletişim Bilgileri</h2>
                <div className="flex flex-col gap-5 text-sm text-foreground-600">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-building-2-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">Firma Adı</p>
                      <p>Atak Sistem Enerji</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">Telefon</p>
                      <a href={`tel:${siteConfig.phoneRaw}`} className="hover:text-primary-600 transition-colors">{siteConfig.phone}</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-whatsapp-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">WhatsApp</p>
                      <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600 transition-colors">WhatsApp'tan Bilgi Al</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">E-posta</p>
                      <p>{siteConfig.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-2-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">Hizmet Bölgesi</p>
                      <p>{siteConfig.serviceArea}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                      <i className="ri-time-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900">Çalışma Saatleri</p>
                      <p>{siteConfig.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <iframe
                    title="Atak Sistem Enerji Konum"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.4!2d27.8826!3d39.6484!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDM4JzU0LjIiTiAyN8KwNTInNTcuNCJF!5e0!3m2!1str!2str!4v0"
                    width="100%"
                    height="280"
                    style={{ border: 0, borderRadius: '8px' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">Bize Yazın</h2>
                {submitted ? (
                  <div className="bg-background-100 rounded-lg p-8 text-center border border-background-200/70">
                    <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-check-line text-primary-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-heading text-xl text-foreground-900 mb-3">Talebiniz Alınmıştır</h3>
                    <p className="text-foreground-600 text-sm mb-6">En kısa sürede sizinle iletişime geçilecektir.</p>
                    <a
                      href={whatsappHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                      Beklemeden WhatsApp'tan Yazın
                    </a>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70 flex flex-col gap-4"
                  >
                    <div>
                      <input
                        type="text"
                        name="ad_soyad"
                        placeholder="Ad Soyad"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        name="telefon"
                        placeholder="Telefon"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="E-posta"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="konu"
                        placeholder="Konu"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="mesaj"
                        placeholder="Mesajınız"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="company_alt"
                      className="form-honeypot"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {formStatus === 'error' && (
                      <p className="text-accent-500 text-xs">{formError || 'Bir hata oluştu. Lütfen tekrar deneyin.'}</p>
                    )}

                    <button
                      type="submit"
                      className="w-full bg-primary-500 text-background-50 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                    >
                      Gönder
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}